import { useEffect, useRef, useState } from "react";
import { LifeBuoy, Loader2, MessageCircle, Send, X } from "lucide-react";
import { useLanguage } from "../i18n";
import Turnstile, { turnstileActive } from "./Turnstile";
import { useHoneypot } from "./Honeypot";

export default function SupportChat() {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [ticketMode, setTicketMode] = useState(false);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketMessage, setTicketMessage] = useState("");
  const [ticketSent, setTicketSent] = useState(false);
  const [ticketCaptcha, setTicketCaptcha] = useState(null);
  const hp = useHoneypot();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const [sessionId] = useState(() => {
    let s = localStorage.getItem("atlas_chat_session");
    if (!s) {
      s = `chat_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem("atlas_chat_session", s);
    }
    return s;
  });

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("atlas-open-chat", handler);
    return () => window.removeEventListener("atlas-open-chat", handler);
  }, []);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t("chat.greeting") }]);
    }
  }, [open, messages.length, t]);

  // auto-scroll: mentre la risposta arriva in streaming la vista segue il fondo,
  // così da mobile non serve trascinare con le dita dentro il pannello
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, ticketMode, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/chat`, {
        method: "POST",
        headers: (() => {
          const h = { "Content-Type": "application/json" };
          const tok = localStorage.getItem("atlas_token");
          if (tok) h.Authorization = `Bearer ${tok}`;
          return h;
        })(),
        body: JSON.stringify({ session_id: sessionId, message: text, lang }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ") || part === "data: [DONE]") continue;
          try {
            const { c } = JSON.parse(part.slice(6));
            if (c) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + c };
                return copy;
              });
            }
          } catch { /* partial json */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t("misc.error") };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const sendTicket = async (e) => {
    e.preventDefault();
    if (busy || ticketSubject.trim().length < 3 || ticketMessage.trim().length < 5) return;
    const tok = localStorage.getItem("atlas_token");
    if (!tok) {
      window.location.href = `/account?next=${encodeURIComponent(window.location.pathname)}`;
      return;
    }
    if (turnstileActive() && !ticketCaptcha) {
      alert(t("misc.captcha"));
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ subject: ticketSubject.trim(), message: ticketMessage.trim(), turnstile_token: ticketCaptcha, ...hp.payload() }),
      });
      if (!res.ok) throw new Error("send failed");
      setTicketSent(true);
    } catch {
      alert(t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const renderMsg = (text, mi, isUser) =>
    (text || "").replace(/\*\*/g, "").split(/(https?:\/\/[^\s)]+)/g).map((part, i) =>
      /^https?:\/\//.test(part) ? (
        <a key={i} data-testid={`chat-link-${mi}-${i}`} href={part.replace(/[.,;:!?]+$/, "")} target="_blank" rel="noreferrer"
           className={`underline font-semibold break-all ${isUser ? "text-white" : "text-[#FF6B35]"}`}>
          {part.replace(/^https?:\/\/(www\.)?/, "").replace(/[.,;:!?]+$/, "")}
        </a>
      ) : part);

  return (
    <>
      <button
        data-testid="support-chat-button"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#FF6B35] text-white shadow-xl shadow-orange-300 flex items-center justify-center hover:bg-[#E8551F] hover:scale-105 transition-all"
        aria-label={t("chat.title")}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div data-testid="support-chat-panel" className="fixed bottom-20 inset-x-3 sm:inset-x-auto sm:bottom-24 sm:right-6 z-50 sm:w-[380px] rounded-3xl bg-white border border-slate-200 shadow-2xl overflow-hidden flex flex-col">
          <div className="bg-[#FF6B35] text-white px-5 py-4">
            <div className="font-display font-bold">{t("chat.title")}</div>
            <div className="text-xs text-white/80 flex items-center gap-1.5 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-emerald-300 inline-block" /> {t("chat.subtitle")}
            </div>
          </div>
          <div ref={listRef} data-testid="support-chat-messages"
            className="h-[52dvh] max-h-[430px] sm:h-80 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] p-4 space-y-3 bg-[#FAF5EE]">
            {ticketMode ? (
              ticketSent ? (
                <div data-testid="ticket-sent" className="h-full flex flex-col items-center justify-center text-center gap-3 px-4">
                  <LifeBuoy className="w-10 h-10 text-[#FF6B35]" />
                  <p className="text-sm text-slate-700 font-medium">{t("ticket.sent")}</p>
                  <button data-testid="ticket-back" onClick={() => { setTicketMode(false); setTicketSent(false); setTicketSubject(""); setTicketMessage(""); }}
                    className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-[#FF6B35] hover:underline">
                    {t("ticket.backChat")}
                  </button>
                </div>
              ) : (
                <form data-testid="ticket-form" onSubmit={sendTicket} className="space-y-3">
                  <input data-testid="ticket-subject" value={ticketSubject} onChange={(e) => setTicketSubject(e.target.value)}
                    placeholder={t("ticket.subject")} maxLength={120}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-base sm:text-sm outline-none focus:border-[#FF6B35] transition-colors" />
                  <textarea data-testid="ticket-message" value={ticketMessage} onChange={(e) => setTicketMessage(e.target.value)}
                    placeholder={t("ticket.message")} rows={6} maxLength={2000}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-base sm:text-sm outline-none focus:border-[#FF6B35] transition-colors resize-none" />
                  <hp.Field />
                  <Turnstile onToken={setTicketCaptcha} />
                  <div className="flex items-center justify-between gap-2">
                    <button type="button" data-testid="ticket-back" onClick={() => setTicketMode(false)}
                      className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-[#FF6B35] transition-colors">
                      {t("ticket.backChat")}
                    </button>
                    <button data-testid="ticket-submit" type="submit" disabled={busy}
                      className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] disabled:opacity-60 transition-colors">
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LifeBuoy className="w-3.5 h-3.5" />}
                      {t("ticket.send")}
                    </button>
                  </div>
                </form>
              )
            ) : (
              <>
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                      m.role === "user" ? "bg-[#FF6B35] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-700 rounded-bl-md"
                    }`}>
                      {m.content ? renderMsg(m.content, i, m.role === "user") : (busy && i === messages.length - 1 ? "…" : "")}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          {!ticketMode && (
            <div className="bg-white border-t border-slate-100 px-4 pt-2">
              <button data-testid="open-ticket" onClick={() => setTicketMode(true)}
                className="inline-flex items-center gap-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-[#FF6B35] transition-colors">
                <LifeBuoy className="w-3.5 h-3.5" /> {t("chat.openTicket")}
              </button>
            </div>
          )}
          <form onSubmit={send} className={`flex border-t border-slate-200 bg-white ${ticketMode ? "hidden" : ""}`}>
            <input
              data-testid="support-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chat.placeholder")}
              className="flex-1 px-4 py-3.5 text-sm outline-none bg-transparent"
            />
            <button data-testid="support-chat-send" type="submit" disabled={busy}
              className="px-4 text-[#FF6B35] hover:text-[#E8551F] disabled:opacity-40 transition-colors">
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
